import cloneDeep from 'lodash-es/cloneDeep'
import filter from 'lodash-es/filter'
import { defineStore, storeToRefs } from 'pinia'
import { markRaw, Ref } from 'vue'
import { Subtask, SubtaskStatus } from '@/api/pathways/subtasks/types'
// nosemgrep: no-legacy-api-store
import apiStore from '@/legacy/store/modules/apiBuilder'
import { useCommunicationsStore } from '@/legacy/store/modules/communications'
import { usePatientStore } from '@/legacy/store/modules/patient'
import { useSmsThreadsApi } from '@/legacy/store/modules/smsThreads'
import { ApiStoreState } from '@/legacy/types/api/apiBuilder'
import { IdMap } from '@/legacy/types/api/store'
import {
  CommunicationType,
  CommunicationParts,
  TextCommunication,
} from '@/legacy/types/communications/communications'
import {
  SmsCommunicationResponse,
  SmsConversation,
  TextingStoreState,
  TokenResponse,
} from '@/legacy/types/communications/texting'
import {
  MapEntityPhoneNumberPersonSms,
  PhoneNumberType,
} from '@/legacy/types/entities/phoneNumbers'
import { SubtaskState } from '@/legacy/types/pathways/subtasks'
import { idMapTransform, toIdMap } from '@/libs/format/objects'
import {
  getContactsPhoneNumbersList,
  getConvosForSmsComms,
  getMediaFromMessages,
  getMessagesForConvos,
  getOpenPointerSubtasks,
  getPatientPhoneNumbersList,
  getPointerSubtaskByCommunicationId,
} from '@/pages/PatientProfile/Tabs/TextingTab/TextingView/shared/sharedTextingParts'
import {
  LIMIT_PER_PHONE_NUMBER,
  SMS_COMM_BY_PHONE_NUMBER_ID_SORT_BY,
  SMS_COMMS_FOR_ACTIVE_THREAD_SORT_BY,
} from '@/pages/PatientProfile/Tabs/TextingTab/TextingView/shared/types'
import { useFlagStore } from '@/store/flags/flags'
import type { Conversation, Message, Participant } from '@twilio/conversations'

// CONST
const POINTER_SUBTASK_FETCH_TIMEOUT = 1000
export const DEFAULT_PER_PAGE = 5
export const DEFAULT_PAGE_NUM = 1
export const SMS_PARTS = [
  CommunicationParts.patients,
  CommunicationParts.smsThread,
]

// Twilio error codes
/* eslint-disable @typescript-eslint/no-magic-numbers */
const TWILIO_ERROR_CODE_TOKEN_INVALID = 20_104
const HTTP_UNAUTHORIZED = 401
const HTTP_NOT_FOUND = 404
/* eslint-enable @typescript-eslint/no-magic-numbers */

/**
 * Twilio connection error shape from the Conversations SDK.
 * The SDK doesn't export these types, so we define based on observed structure.
 */
interface TwilioConnectionError {
  terminal?: boolean
  message?: string
  httpStatusCode?: number | null
  errorCode?: number | null
}

const initialState = (): TextingStoreState => ({
  // Twilio
  _conversationsClient: null,
  conversationsClientError: null,
  conversationsById: {},
  clientLoading: false,

  // Active Conversation View
  activeConversation: null,
  _messageAddedHandler: null,
  activeCommunication: null,
  smsCommsForActiveConversation: null,
  messages: [],
  conversations: [],
  messageToSend: '',
  mediaLinks: {},
  messagesLoading: false,
  conversationsLoading: false,
  messageDeliveryStatusMapBySid: {},
  conversationsError: null,
  messagesError: null,

  // Texting Inbox View
  smsCommsByPhoneNumberId: null,
  messageMapByConvoSid: {},
  inboxMessagesLoading: false,
  inboxMessagesError: null,
  inboxConversationsError: null,

  // Shared Data
  textableIndividuals: [],
  allTextablePhoneNumberIds: [],
})

/*
 * Pinia's storeToRefs return type can't be inferred against this store since
 * Twilio state (e.g. _conversationsClient, activeConversation, etc.)
 * causes a "Type instantiation is excessively deep and possibly infinite" (TS2589).
 *
 * Callers should do `storeToRefs(useTextingStore() as any) as TextingStoreRefs`
 * instead of calling storeToRefs directly.
 */
export type TextingStoreRefs = {
  [K in keyof TextingStoreState]: Ref<TextingStoreState[K]>
}

export const useTextingStore = defineStore('textingV2', {
  state: () => cloneDeep(initialState()),
  actions: {
    // Note: this is typed as `any` to avoid pulling in the dependency
    // needed for type checking prior to when this module is `init`'d
    initConversationsClient(conversationsClient: object | undefined) {
      this.conversationsClientError = null
      /**
       * Skip converting the ConversationsClient to a proxy. Prevents Vue's deep reactivity,
       * but avoids errors caused by converting readonly properties of ConversationsClient.
       * See more: https://v3.vuejs.org/api/basic-reactivity.html#toraw
       */
      // @ts-ignore
      this._conversationsClient = conversationsClient
        ? markRaw(conversationsClient)
        : conversationsClient
    },
    setConversationsClientError(error: Error | null) {
      this.conversationsClientError = error
    },
    setConversations(conversation: Conversation) {
      const foundIndex = this.conversations?.findIndex(
        (convo) => convo.sid === conversation.sid
      )
      if (foundIndex) {
        this.conversations[foundIndex] = conversation
      } else {
        this.conversations = [...this.conversations, conversation]
      }
    },
    removeConversation(thisConversation: any) {
      this.conversations = this.conversations.filter(
        (conversation: any) => conversation !== thisConversation
      )
    },
    async getConversation(
      conversationSid: string
    ): Promise<SmsConversation | null> {
      return (
        (await this._conversationsClient?.getConversationBySid(
          conversationSid
        )) ?? null
      )
    },
    /**
     * Removes the `messageAdded` listener from whatever conversation it
     * was last attached to. Twilio's Conversation extends EventEmitter, so
     * a forgotten listener stays alive for the lifetime of the conversation
     * object and fires on every incoming message — over a long session
     * with many activeConversation switches, the same handler runs N
     * times per message.
     */
    detachMessageAddedHandler() {
      if (this._messageAddedHandler && this.activeConversation) {
        this.activeConversation.off('messageAdded', this._messageAddedHandler)
      }
      this._messageAddedHandler = null
    },
    async setActiveConversation(communication: TextCommunication) {
      /*
       * Short-circuit when we're already on this comm and the Twilio
       * Conversation reference is live AND still matches the comm's
       * current conversation set. The post-send refetch cascade fires
       * this with the same comm; without this guard, we'd detach the
       * messageAdded handler, re-fetch the Conversation, re-attach, and
       * kick off a fire-and-forget setAllMessagesRead — opening a race
       * window where a follow-up sendMessage hits a half-set-up
       * Conversation and Twilio rejects with Bad Request (TEA texting
       * "every other send" bug).
       *
       * Guard conditions, in order:
       * - same comm (by id)
       * - activeConversation is live (otherwise we need to rebuild)
       * - activeConversation.sid is the comm's only conversation id
       *
       * The single-id check is intentional: the multi-id path below picks
       * "most recent by lastMessage.dateCreated", which requires fetching
       * all of them to decide. If a new sid was appended or rotated in,
       * the live activeConversation could no longer be the canonical one
       * — fall through to full setup so the right one gets selected.
       *
       * We still refresh `activeCommunication` so updated server-side
       * fields (responsibleStaffId, completedDatetime, smsThread) flow
       * into the UI.
       */
      const incomingTwilioIds = communication.smsThread.twilioConversationIds
      if (
        this.activeCommunication?.communicationId ===
          communication.communicationId &&
        this.activeConversation &&
        incomingTwilioIds.length === 1 &&
        this.activeConversation.sid === incomingTwilioIds[0]
      ) {
        this.activeCommunication = communication
        return
      }
      /*
       * `this.activeConversation` still points at the previous conversation
       * here; detach before we overwrite it so the listener comes off the
       * right object.
       */
      this.detachMessageAddedHandler()
      this.activeCommunication = communication

      const twilioConversationIds =
        communication.smsThread.twilioConversationIds

      if (!twilioConversationIds.length) {
        this.conversationsError = `Error: No conversation IDs found for this communication ${communication.communicationId}`
        this.activeConversation = null
        return
      }

      try {
        if (twilioConversationIds.length === 1) {
          // @ts-ignore
          this.activeConversation = await this.getConversation(
            twilioConversationIds[0]
          )
        } else {
          const conversations = await Promise.all(
            twilioConversationIds.map(
              async (conversationSid: string) =>
                await this.getConversation(conversationSid)
            )
          )
          const sortedConversations = conversations.sort(
            (a: SmsConversation | null, b: SmsConversation | null) => {
              const aDate = a?.lastMessage?.dateCreated
              const bDate = b?.lastMessage?.dateCreated
              return (
                (bDate instanceof Date ? bDate.getTime() : 0) -
                (aDate instanceof Date ? aDate.getTime() : 0)
              )
            }
          )
          this.activeConversation = sortedConversations[0] ?? null
        }
        if (this.activeConversation) {
          const handler = async (message: Message) => {
            // For testing in prod/staging: Log when a message is added to a conversation
            console.debug(
              `TWILIO: message ${message.sid} added for Conversation ${message.conversation.sid}`
            )
            await this.markActiveSmsThreadIncludesManualTexts()
            // The call above only patches the thread's `includesManualTexts`
            // flag. This is what reloads the messages on screen.
            await this.refreshActiveThreadForConversation(
              message.conversation.sid
            )
          }
          this.activeConversation.on('messageAdded', handler)
          this._messageAddedHandler = handler
          // Do not attempt to set messages read if the conversation is closed
          // This just flags an error in the console
          if (this.activeConversation.state?.current !== 'closed') {
            void this.activeConversation.setAllMessagesRead()
          }
        }
      } catch (err) {
        console.error('Error: Cannot set active conversation ...', err)
        this.activeConversation = null
        return
      }
    },
    async refreshActiveCommunication() {
      const communicationId = this.activeCommunication?.communicationId
      if (communicationId) {
        const communication =
          await useCommunicationsStore().fetchCommunicationById(communicationId)
        this.activeCommunication = (communication as TextCommunication) ?? null
      }
    },
    /**
     * Reloads the open thread's messages when the Conversations SDK tells us
     * something arrived. We read the messages from Twilio but decide when to
     * reload them off the DB communication record, and that record doesn't
     * change when a message is sent or received, so without this an inbound or
     * freshly sent message sits invisible until the user refreshes by hand.
     *
     * We match the event's sid against the comm's whole `twilioConversationIds`
     * list rather than its newest sid, since a thread accumulates sids over its
     * life and a message can land on any of them.
     *
     * Gated on `beSmsSendEnabled` so it ships with the backend send cutover.
     * @param eventConversationSid
     */
    async refreshActiveThreadForConversation(eventConversationSid: string) {
      const { beSmsSendEnabled } = storeToRefs(useFlagStore())
      if (!beSmsSendEnabled.value) {
        return
      }
      const activeTwilioIds =
        this.activeCommunication?.smsThread.twilioConversationIds ?? []
      if (!activeTwilioIds.includes(eventConversationSid)) {
        return
      }
      await this.getAndSetMessagesForIndividual()
      await this.mapMessagesToDeliveryStatus()
    },

    async markActiveSmsThreadIncludesManualTexts() {
      const communicationId = this.activeCommunication?.communicationId
      if (
        communicationId &&
        !this.activeCommunication?.smsThread?.includesManualTexts
      ) {
        await useSmsThreadsApi().partialUpdate({
          ids: [communicationId],
          body: { includesManualTexts: true },
        })
        await this.refreshActiveCommunication()
      }
    },

    async init(vueQueryToken?: string) {
      // if there's a working conversationsClient, don't reinitialize
      if (this._conversationsClient && !this.conversationsClientError) {
        console.warn('Existing Twilio conversationsClient, skipping reinit...')
        return
      }

      console.warn('Initializing Twilio conversationsClient...')
      this.clientLoading = true
      let conversationsClient
      try {
        // Use token from Vue Query if provided, otherwise fetch via old method
        const token = vueQueryToken ?? (await this.getToken())

        // WARNING: This is a large dependency! Imported inline so it's only
        // pulled in when we actually init this module, rather than
        // upon app startup. This improves bundle code splitting
        // and initial page load time
        const { Client: ConversationsClient } =
          await import('@twilio/conversations')
        // `import.meta.env.MODE` is the Vite build mode (set via `--mode`,
        // matches the corresponding `.env.<mode>` file: dev, prod, staging,
        // e2e). Locked at build time, not runtime.
        conversationsClient = new ConversationsClient(token as string, {
          logLevel: import.meta.env.MODE === 'staging' ? 'trace' : 'silent',
        })
      } catch (err) {
        if (err instanceof Error) {
          this.setConversationsClientError(err)
        }
        return
      }

      this.initConversationsClient(conversationsClient)
      // A ref, not the unwrapped boolean: the handlers below outlive this
      // function, so they have to read the flag when the event fires.
      const { beSmsSendEnabled } = storeToRefs(useFlagStore())
      conversationsClient.on(
        'conversationUpdated',
        async ({ conversation, updateReasons }: any) => {
          // For testing in prod/staging: Log when a conversation is updated
          console.debug('TWILIO: Conversation updated', conversation.sid)
          console.debug('TWILIO: Update reasons', updateReasons)

          // reset any errors on successful registration
          this.setConversationsClientError(null)
          this.setConversations(conversation)

          if (updateReasons?.length) {
            const messageAdded = updateReasons.includes('lastMessage')
            if (messageAdded) {
              await this.fetchAndSetSMSCommByPhoneNumberIds()
              // The fetch above refreshes the thread list. This one refreshes
              // the messages inside the thread that's currently open.
              await this.refreshActiveThreadForConversation(conversation.sid)
            }
          }
        }
      )
      this.clientLoading = false

      conversationsClient.on(
        'participantJoined',
        async (participant: Participant) => {
          // For testing in prod/staging: Log when a participant joins the conversation
          // e.g. updating responsible staff ID
          console.debug(
            'TWILIO: participant joined',
            participant.conversation.sid
          )
          // Re-fetch data when changing responsible staff / owner for SMS communications
          await this.fetchAndSetSMSCommByPhoneNumberIds()
        }
      )

      conversationsClient.on(
        'conversationJoined',
        async (conversation: Conversation) => {
          // For testing in prod/staging: Log when a conversation is joined
          console.debug('TWILIO: Conversation joined', conversation.sid)
          this.setConversationsClientError(null)
          this.setConversations(conversation)
          // A first message from a number nobody has texted yet arrives here as
          // a freshly joined conversation, not as `messageAdded` on one we're
          // already watching, so this is the only event that can surface it.
          if (beSmsSendEnabled.value) {
            await this.fetchAndSetSMSCommByPhoneNumberIds()
            await this.refreshActiveThreadForConversation(conversation.sid)
          }
        }
      )

      conversationsClient.on(
        'conversationLeft',
        async (conversation: Conversation) => {
          // For testing in prod/staging: Log when a conversation is left
          console.debug('TWILIO: Conversation left', conversation.sid)
          console.debug('TWILIO: Conversation left convo', conversation)
          console.debug(
            'TWILIO: Conversation left comm',
            this.activeCommunication
          )

          console.debug('--Before settimeout--')
          setTimeout(
            async () => await this.callPointerSubtasks(),
            POINTER_SUBTASK_FETCH_TIMEOUT
          )
          console.debug('--After settimeout--')

          // reset any errors on successful registration
          this.setConversationsClientError(null)
          this.removeConversation(conversation)
        }
      )

      conversationsClient.on(
        'connectionError',
        (err: TwilioConnectionError) => {
          // Log the raw error for debugging in production
          console.error(
            'TWILIO: conversationsClient connection error:',
            JSON.stringify({
              errorCode: err.errorCode,
              httpStatusCode: err.httpStatusCode,
              message: err.message,
              terminal: err.terminal,
            })
          )

          // Provide more actionable error messages to users
          let userMessage: string
          if (
            err.errorCode === TWILIO_ERROR_CODE_TOKEN_INVALID ||
            err.httpStatusCode === HTTP_UNAUTHORIZED
          ) {
            // Token expired/invalid
            userMessage =
              'Texting session expired. Please refresh the page to reconnect.'
          } else if (err.httpStatusCode === HTTP_NOT_FOUND) {
            // Service not found
            userMessage =
              'Texting service unavailable. Please contact support if this persists.'
          } else if (err.message?.includes('Socket error')) {
            // Network/connection issues
            userMessage =
              'Connection to texting service lost. Check your internet connection and try refreshing.'
          } else {
            // For unknown errors, show the Twilio message if available
            userMessage = err.message ?? 'Connection error occurred.'
          }

          this.setConversationsClientError(new Error(userMessage))
        }
      )

      conversationsClient.on('tokenAboutToExpire', () => {
        console.warn('TWILIO: Token about to expire, refreshing...')
        void this.refreshToken()
      })

      conversationsClient.on('tokenExpired', () => {
        console.error('TWILIO: Token expired, refreshing...')
        void this.refreshToken()
      })

      conversationsClient.on(
        'messageUpdated',
        async ({ message, updateReasons }) => {
          // For testing in prod/staging: Log when a message is updated
          console.debug('TWILIO: Message updated', message, updateReasons)
        }
      )
    },
    async callPointerSubtasks(): Promise<void> {
      if (
        this.activeCommunication &&
        this.activeCommunication.patients?.length
      ) {
        const currentCommId = this.activeCommunication.communicationId
        const patientId = this.activeCommunication.patients[0].entityId
        try {
          await getPointerSubtaskByCommunicationId(currentCommId, patientId)
          await getOpenPointerSubtasks(patientId)
        } catch (err) {
          console.error('Error fetching pointer subtasks:', err)
        }
      }
    },
    async getToken() {
      let data: TokenResponse | null
      try {
        data = await useTwilioTokenApi().retrieve({ ids: [] })
      } catch (err) {
        console.error(err)
        if (err instanceof Error) {
          this.conversationsClientError = err
        }
        return
      }
      return data?.token
    },
    /**
     * Fetches a fresh Twilio token and updates the existing
     * conversations client. Called from `tokenAboutToExpire` and
     * `tokenExpired` events on the conversations client.
     */
    async refreshToken() {
      try {
        const token = await this.getToken()
        if (!token) {
          console.error('TWILIO: Failed to refresh token - no token returned')
          this.setConversationsClientError(
            new Error('Failed to fetch Twilio token')
          )
          return
        }
        if (this._conversationsClient) {
          await this._conversationsClient.updateToken(token)
          console.warn('TWILIO: Token refreshed successfully')
          // Clear any previous errors on successful refresh
          this.setConversationsClientError(null)
        }
      } catch (err) {
        console.error('TWILIO: Error refreshing token:', err)
        if (err instanceof Error) {
          this.setConversationsClientError(err)
        }
      }
    },
    /**
     * Below fetch method has several implicit params.
     * This function is used to fetch most recent sms
     * communication per phone number id passed in query.
     *
     * It will always grab 1 sms communication per phone number
     * and sorted by most recently updated sms thread; thus,
     * phone number ids are always required.
     * @param root0
     * @param root0.phoneNumberIds
     * @param root0.entityIds
     * @param root0.perPage
     * @param root0.pageNumber
     * @param root0.fetchAll
     */
    async getSmsCommByPhoneNumberIds({
      phoneNumberIds,
      entityIds,
      perPage = DEFAULT_PER_PAGE,
      pageNumber = DEFAULT_PAGE_NUM,
      fetchAll = false,
    }: {
      phoneNumberIds: string[]
      entityIds?: null | string[] | undefined
      perPage?: null | number | undefined
      pageNumber?: null | number | undefined
      fetchAll?: boolean
    }) {
      let results
      if (fetchAll) {
        results = await useSmsCommByPhoneNumberIdApi().listAll({
          params: {
            limit_per_phone_number: LIMIT_PER_PHONE_NUMBER,
            filter_phone_number_ids: phoneNumberIds,
            filter_types: CommunicationType.Text,
            sort_by: SMS_COMM_BY_PHONE_NUMBER_ID_SORT_BY,
            parts: SMS_PARTS,
            ...(entityIds ? { filter_patient_ids: entityIds } : {}),
          },
        })
      } else {
        results = await useSmsCommByPhoneNumberIdApi().list({
          params: {
            limit_per_phone_number: LIMIT_PER_PHONE_NUMBER,
            filter_phone_number_ids: phoneNumberIds,
            filter_types: CommunicationType.Text,
            sort_by: SMS_COMM_BY_PHONE_NUMBER_ID_SORT_BY,
            page_length: perPage,
            page_number: pageNumber,
            parts: SMS_PARTS,
            ...(entityIds ? { filter_patient_ids: entityIds } : {}),
          },
        })
      }
      if (results?.data.length) {
        this.smsCommsByPhoneNumberId = results
        await this.setMessageMapByConvoSid()
        return results.data
      }
      return null
    },
    /**
     * This function is used to fetch first 10 communications
     * for current selected individual in texting inbox UI.
     * @param root0
     * @param root0.phoneNumberIds
     * @param root0.entityIds
     * @param root0.perPage
     * @param root0.pageNumber
     */
    async getSmsCommsForActiveThread({
      phoneNumberIds,
      entityIds,
      perPage = DEFAULT_PER_PAGE,
      pageNumber = DEFAULT_PAGE_NUM,
    }: {
      phoneNumberIds?: null | string[] | undefined
      entityIds?: null | string[] | undefined
      perPage?: null | number | undefined
      pageNumber?: null | number | undefined
    }) {
      const results = await useTextingCommsApiV2().list({
        params: {
          page_length: perPage,
          page_number: pageNumber,
          filter_types: CommunicationType.Text,
          sort_by: SMS_COMMS_FOR_ACTIVE_THREAD_SORT_BY,
          parts: SMS_PARTS,
          ...(entityIds ? { filter_patient_ids: entityIds } : {}),
          ...(phoneNumberIds
            ? { filter_phone_number_ids: phoneNumberIds }
            : {}),
        },
      })
      if (results?.data.length) {
        this.smsCommsForActiveConversation = results
        await this.setActiveConversation(results.data[0])
        return {
          data: results.data,
          total: results.queryMetadata.total,
        }
      }
      return null
    },
    /**
     * This function is used to fetch additional sets of
     * 10 sms communications for current selected individual in texting inbox UI.
     * This method is triggered if "Load more" is clicked in the
     * AllSms.vue component.
     * @param root0
     * @param root0.phoneNumberIds
     * @param root0.entityIds
     * @param root0.perPage
     * @param root0.pageNumber
     */
    async loadMoreSmsCommsForActiveThread({
      phoneNumberIds,
      entityIds,
      perPage = DEFAULT_PER_PAGE,
      pageNumber = DEFAULT_PAGE_NUM,
    }: {
      phoneNumberIds: null | string[]
      entityIds: null | string[]
      pageNumber: null | number
      perPage?: null | number | undefined
    }) {
      const results = await useTextingCommsApiV2().list({
        params: {
          page_length: perPage,
          page_number: pageNumber,
          filter_types: CommunicationType.Text,
          sort_by: SMS_COMMS_FOR_ACTIVE_THREAD_SORT_BY,
          parts: SMS_PARTS,
          ...(entityIds ? { filter_patient_ids: entityIds } : {}),
          ...(phoneNumberIds
            ? { filter_phone_number_ids: phoneNumberIds }
            : {}),
        },
      })
      if (
        results?.data.length &&
        this.smsCommsForActiveConversation?.data.length
      ) {
        this.smsCommsForActiveConversation.data.push(...results.data)
      }
    },
    /**
     *
     * Helper method to set up a customized list of data from
     * contacts, phone numbers and person data.
     * This data is shared across all texting thymeline child components
     * in order to best identify which textable individual is currently
     * selected for texting.
     */
    setUpTextableIndividualsAndNumbers() {
      const { contacts, phoneNumbers, person } = storeToRefs(usePatientStore())
      let patientPhoneNumbersList: MapEntityPhoneNumberPersonSms[] = []
      let contactsPhoneNumbersList: MapEntityPhoneNumberPersonSms[] = []
      if (phoneNumbers.value && person.value) {
        // Set up patient's phone numbers list using person and phone number data
        // from patient store: [{ person, ...phoneNumber1 }, { person, ...phoneNumber2 },... ]
        patientPhoneNumbersList = getPatientPhoneNumbersList(
          phoneNumbers.value,
          person.value
        )
      }
      if (contacts.value) {
        /**
         *
         * If patient has contacts, set up list of contacts and their phone numbers list:
         * [
         *   { contactPerson1, ...phoneNumber1 },
         *   { contactPerson1, ...phoneNumber2 },
         *   { contactPerson2, ...phoneNumber1 },
         *   ...
         * ]
         * Each contact data will have additional info `relationshipToPatient`
         */
        contactsPhoneNumbersList = getContactsPhoneNumbersList(contacts.value)
      }

      // Join both sets of data to a list of specific data:
      // person, phone number and contact information
      const allEntities = [
        ...patientPhoneNumbersList,
        ...contactsPhoneNumbersList,
      ]

      // Set the store's value for `textableIndividuals` while filtering out
      // any phone numbers that are of type LANDLINE
      this.textableIndividuals = filter(
        allEntities,
        (entity: MapEntityPhoneNumberPersonSms) =>
          entity.phoneNumber.type !== PhoneNumberType.LANDLINE &&
          entity.phoneNumber.type !== PhoneNumberType.OTHER
      )

      // Using above data, set all textable phone number ids as a string array
      // for easy access across components
      this.allTextablePhoneNumberIds = this.textableIndividuals.map(
        (textable) => textable.phoneNumberId
      )
    },
    /**
     *
     * Function that fetches most recent sms communications
     * per phone number ids passed as query param.
     * Also sets the smsThreadByPhoneNumberMap ref:
     * e.g. { <phoneNumberId>: <smsThread>  }
     * @param patientId
     * @param fetchAll
     */
    async fetchAndSetSMSCommByPhoneNumberIds(
      patientId: string | undefined = undefined,
      fetchAll = false
    ) {
      useTextingStore().setUpTextableIndividualsAndNumbers()
      if (this.allTextablePhoneNumberIds) {
        await useTextingStore().getSmsCommByPhoneNumberIds({
          phoneNumberIds: this.allTextablePhoneNumberIds,
          entityIds: [patientId ?? usePatientStore().patient?.entityId ?? ''],
          fetchAll,
        })
      }
    },
    /**
     *
     * Function that gets conversations for fetched sms communications and each conversation's messages.
     * When messages are successfully fetched, sets messageMapByConvoSid:
     * e.g. { <conversationSid>: [Message, Message, Message]  }
     *
     * Also sets textingInboxDataError ref if any errors occurred when fetching conversations or messages
     */
    async setMessageMapByConvoSid() {
      if (this.smsCommsByPhoneNumberId) {
        this.conversationsLoading = true
        const conversationData = await getConvosForSmsComms(
          this.smsCommsByPhoneNumberId.data
        )
        if (conversationData?.convos?.length) {
          for (let i = 0; i < conversationData.convos.length; i++) {
            const convo = conversationData.convos[i]
            // IMPROVEME(MT-2755): find a way to parallelize Twilio calls if it's even possible
            this.inboxMessagesLoading = true
            const convoMessagesData = await getMessagesForConvos([convo])
            if (convoMessagesData?.messages.length) {
              this.messageMapByConvoSid[convo.sid] = convoMessagesData.messages
            }
            if (convoMessagesData?.error) {
              this.inboxMessagesError = convoMessagesData.error
            }
          }
        } else if (conversationData?.error) {
          this.inboxConversationsError = conversationData.error
        }

        this.conversationsLoading = false
        this.inboxMessagesLoading = false
      }
    },
    /**
     * Function that gets all conversations for given sms communications
     * and sets the conversations ref and messagesError ref
     */
    async getAndSetConversationsForIndividual() {
      if (this.smsCommsForActiveConversation) {
        this.conversationsLoading = true
        this.messagesLoading = true
        const conversationData = await getConvosForSmsComms(
          this.smsCommsForActiveConversation.data
        )
        if (conversationData) {
          this.conversations = conversationData.convos
          this.conversationsError = conversationData.error
        }
      }
      this.conversationsLoading = false
    },
    /**
     *
     * Function that retrieves all messages for passed conversations list
     * and also sets:
     * - messages ref --> all messages for all conversations
     * - messagesError ref --> any errors that occurred when fetching messages
     * - messagesMappedByDate ref --> mapping of messages to date string of sent date
     */
    async getAndSetMessagesForIndividual() {
      if (this.conversations.length > 0) {
        const messagesData = await getMessagesForConvos(
          this.conversations as Conversation[]
        )
        this.messages = messagesData?.messages ?? []
        this.messagesError = messagesData?.error ?? null
      }
      this.messagesLoading = false
      await this.getAndSetMessageMediaForIndividual()
    },
    /**
     *
     * Function that retrieves all media from passed messages list
     * and sets the mediaLinks ref
     */
    async getAndSetMessageMediaForIndividual() {
      this.mediaLinks = await getMediaFromMessages(
        this.messages as Message[],
        this.mediaLinks
      )
    },

    /**
     *
     * Function to create a mapping of message sid to delivery status
     * e.g. { <MESSAGE_SID>: [DeliveryReceipt]  }
     */
    async mapMessagesToDeliveryStatus() {
      const mapping: { [key: string]: Message[] } = {}
      for (const message of this.messages) {
        const sid = message.sid
        if (!mapping[sid]) {
          mapping[sid] = await message.getDetailedDeliveryReceipts()
        }
      }
      this.messageDeliveryStatusMapBySid = mapping
    },
    /**
     * Drops the message/conversation lists for the current thread without
     * touching the active Twilio refs. Used by the post-send refetch path
     * so the active Twilio conversation reference stays valid across the
     * async rebind window.
     */
    clearMessagesLists() {
      this.conversations = []
      this.messages = []
      this.mediaLinks = {}
      this.smsCommsForActiveConversation = null
    },
    /**
     * Drops just the active Twilio refs (and detaches the messageAdded
     * listener). Used when the URL no longer points at a comm (e.g. user
     * navigated away from a thread).
     */
    clearActiveConversationRefs() {
      this.detachMessageAddedHandler()
      this.activeCommunication = null
      this.activeConversation = null
    },
    /**
     * Composite reset for full thread switches: clears both the message
     * lists and the active Twilio refs in one call. Equivalent to
     * `clearMessagesLists() + clearActiveConversationRefs()` — the two
     * narrower methods exist for callers (post-send refetch, navigated
     * away) that only need one half.
     */
    clearSetConvosAndMessages() {
      this.clearMessagesLists()
      this.clearActiveConversationRefs()
    },
    /**
     * Drops every error string the texting UI may surface (active thread
     * + inbox). Used at the start of any flow that's about to refetch.
     * Also clears conversationsClientError so token/connection errors
     * don't permanently disable the message box (user can retry send).
     */
    clearErrorStates() {
      this.messagesError = null
      this.conversationsError = null
      this.inboxConversationsError = null
      this.inboxMessagesError = null
      this.conversationsClientError = null
    },
    /**
     * Drops the typed-but-unsent message draft. Called on patient change
     * so a draft from one patient can't bleed into another's thread.
     */
    clearMessageToSend() {
      this.messageToSend = ''
    },
    /**
     * Drops the Twilio `_conversationsClient` and its derivatives. The
     * client is keyed to the logged-in user (not the patient), so this is
     * only safe at user-scope boundaries — full app reset, sign-out — not
     * patient transitions.
     */
    clearTwilioClient() {
      this._conversationsClient = null
      this.conversationsClientError = null
      this.conversationsById = {}
    },
    /**
     * Clears all patient-scoped state but preserves the Twilio
     * `_conversationsClient` (which is keyed to the logged-in user, not
     * the patient). Call this when crossing a patient boundary so stale
     * refs from the previous patient — most importantly
     * `activeConversation`, `activeCommunication`, and `messageToSend` —
     * can't be used before the next patient's data finishes loading.
     */
    resetForPatientChange() {
      this.clearSetConvosAndMessages()
      this.clearErrorStates()
      this.clearMessageToSend()

      this.textableIndividuals = []
      this.allTextablePhoneNumberIds = []

      this.smsCommsByPhoneNumberId = null
      this.messageMapByConvoSid = {}
      this.messageDeliveryStatusMapBySid = {}
    },
    reset() {
      this.resetForPatientChange()
      this.clearTwilioClient()
    },
  },
})

const transformSmsCommunications = ({
  data,
  queryMetadata,
}: SmsCommunicationResponse): Pick<
  ApiStoreState<TextCommunication, IdMap<TextCommunication>>,
  'data' | 'queryMetadata'
> => {
  return {
    data: data?.length ? toIdMap(data, 'communicationId') : null,
    queryMetadata: queryMetadata,
  }
}

export const useTextingCommsApiV2 = apiStore<
  TextCommunication,
  IdMap<TextCommunication>,
  TextCommunication,
  SmsCommunicationResponse
>('textCommunicationsApiV2', '/api/communications', {
  transformData: (d: SmsCommunicationResponse) => transformSmsCommunications(d),
})

export const useSmsCommByPhoneNumberIdApi = apiStore<
  TextCommunication,
  IdMap<TextCommunication>,
  TextCommunication,
  SmsCommunicationResponse
>('smsCommByPhoneNumberIdApi', '/api/communications', {
  transformData: (d: SmsCommunicationResponse) => transformSmsCommunications(d),
})

export const useTwilioTokenApi = apiStore<TokenResponse>(
  'twilioTokenApi',
  '/api/communications/texting/token',
  {}
)

// pointer subtasks ------

// take subtask data and only return subtasks pointed at open text comms
const transformPointerSubtaskData = (
  data: Subtask[]
): Pick<SubtaskState, 'data'> => {
  const filteredSubtasks = data.filter(
    (subtask: Subtask) => subtask.pointerToCommId
  )
  return {
    ...idMapTransform({}, 'data', 'pointerToCommId', filteredSubtasks),
  }
}

export const useOpenTextingSubtaskApi = apiStore<Subtask, IdMap<Subtask>>(
  'openTextingSubtaskApi',
  '/api/subtasks',
  {
    transformData: (d: { data: Subtask[] }) =>
      transformPointerSubtaskData(d.data),
    params: {
      filter_subtask_status: [
        SubtaskStatus.OPEN_ASSIGNED,
        SubtaskStatus.OPEN_UNASSIGNED,
      ],
    },
  }
)

export const useTextingSubtaskByIdApi = apiStore<Subtask, IdMap<Subtask>>(
  'textingSubtaskByIdApi',
  '/api/subtasks',
  {
    transformData: (d: { data: Subtask[] }) =>
      transformPointerSubtaskData(d.data),
  }
)
